import { useEffect, useMemo } from 'react'
import {
    Button,
    ButtonGroup,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    SimpleGrid,
    Stack,
    Text
} from '@chakra-ui/react'
import { FiRepeat } from 'react-icons/fi'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { ChildRecord } from '@/shared/api/rest/types'
import { UpdateChildPayload } from '@/shared/api/rest/services/children.service'
import { useUpdateChild } from '../hooks/useUpdateChild'
import { getChildName } from '../types'

/** The earliest date of birth a child record can plausibly carry. */
const MIN_DATE_OF_BIRTH = '1990-01-01'

const GENDER_OPTIONS = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' }
]

const BLOOD_GROUP_OPTIONS = [
    'A +',
    'A -',
    'B +',
    'B -',
    'O +',
    'O -',
    'AB +',
    'AB -'
].map((value) => ({ value, label: value.replace(' ', '') }))

const GENOTYPE_OPTIONS = ['AA', 'AS', 'SS', 'AC', 'SC'].map((value) => ({
    value,
    label: value
}))

/**
 * Records predate these option lists — gender arrives as "Male" as often as
 * "male", and blood groups without the space. Keeping the stored value in the
 * list means opening the form to fix a name doesn't silently rewrite fields
 * nobody touched.
 */
const withCurrentValue = (
    options: Array<{ value: string; label: string }>,
    current?: string | null
) => {
    const value = current?.trim()
    if (!value || options.some((option) => option.value === value)) return options
    return [{ value, label: value }, ...options]
}

/** `<input type="date">` needs a bare YYYY-MM-DD. */
const toDateInputValue = (dateOfBirth?: string | null): string => {
    const value = dateOfBirth?.trim()
    if (!value) return ''
    // Slice ISO strings rather than round-tripping through Date — parsing
    // "2024-08-08" as UTC and formatting it locally lands on the 7th for
    // anyone west of Greenwich, which is exactly the kind of off-by-one DOB
    // this form exists to correct.
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(value)
    if (match) return match[1]
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return ''
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`
}

const todayInputValue = () => toDateInputValue(new Date().toISOString())

const schema = yup.object({
    firstName: yup.string().trim().required('First name is required'),
    middleName: yup.string().trim(),
    lastName: yup.string().trim().required('Surname is required'),
    dateOfBirth: yup
        .string()
        .trim()
        .required('Date of birth is required')
        // A DOB in the future puts the child in the wrong age group and is one
        // of the two mistakes this form exists to fix, so it is rejected here
        // rather than left to the backend.
        .test(
            'not-in-the-future',
            'Date of birth cannot be in the future',
            (value) => !value || value <= todayInputValue()
        )
        .test(
            'not-implausibly-old',
            `Date of birth cannot be before ${MIN_DATE_OF_BIRTH}`,
            (value) => !value || value >= MIN_DATE_OF_BIRTH
        ),
    gender: yup.string().trim(),
    bloodGroup: yup.string().trim(),
    genotype: yup.string().trim(),
    allergies: yup.string().trim()
})

type FormValues = yup.InferType<typeof schema>

const toFormValues = (child: ChildRecord): FormValues => ({
    firstName: child.firstName ?? '',
    middleName: child.middleName ?? '',
    lastName: child.lastName ?? '',
    dateOfBirth: toDateInputValue(child.dateOfBirth),
    gender: child.gender ?? '',
    bloodGroup: child.bloodGroup ?? '',
    genotype: child.genotype ?? '',
    allergies: child.allergies?.join(', ') ?? ''
})

const splitAllergies = (value: string) =>
    value
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)

/**
 * Build a PATCH body from only the fields that actually changed, so correcting
 * a surname doesn't rewrite the other columns with re-serialised copies of what
 * was already there.
 */
const buildPayload = (values: FormValues, original: FormValues): UpdateChildPayload => {
    const payload: UpdateChildPayload = {}
    const trim = (value?: string) => (value ?? '').trim()

    if (trim(values.firstName) !== trim(original.firstName))
        payload.firstName = trim(values.firstName)
    if (trim(values.middleName) !== trim(original.middleName))
        payload.middleName = trim(values.middleName)
    if (trim(values.lastName) !== trim(original.lastName))
        payload.lastName = trim(values.lastName)
    if (trim(values.dateOfBirth) !== trim(original.dateOfBirth))
        payload.dateOfBirth = trim(values.dateOfBirth)
    if (trim(values.gender) !== trim(original.gender))
        payload.gender = trim(values.gender)
    if (trim(values.bloodGroup) !== trim(original.bloodGroup))
        payload.bloodGroup = trim(values.bloodGroup)
    if (trim(values.genotype) !== trim(original.genotype))
        payload.genotype = trim(values.genotype)
    if (trim(values.allergies) !== trim(original.allergies))
        payload.allergies = splitAllergies(values.allergies ?? '')

    return payload
}

type Props = { child: ChildRecord; isOpen: boolean; onClose: () => void }

/**
 * Correct a child's registry details — names and date of birth above all. Both
 * are entered by parents at sign-up and both are routinely wrong: first name
 * and surname swapped, or a date of birth off by a year, which lands the child
 * in the wrong age group at check-in.
 */
export default function EditChildModal({ child, isOpen, onClose }: Props) {
    const { updateChild, saving } = useUpdateChild()
    const original = useMemo(() => toFormValues(child), [child])

    const {
        register,
        handleSubmit,
        reset,
        getValues,
        setValue,
        formState: { errors }
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
        defaultValues: original
    })

    // Reopening the modal — or the card refreshing underneath it — must show
    // what is on record now, not whatever was last typed into the form.
    useEffect(() => {
        if (isOpen) reset(original)
    }, [isOpen, original, reset])

    const swapNames = () => {
        const { firstName, lastName } = getValues()
        setValue('firstName', lastName ?? '', { shouldValidate: true })
        setValue('lastName', firstName ?? '', { shouldValidate: true })
    }

    const onSubmit = async (values: FormValues) => {
        const payload = buildPayload(values, original)
        if (Object.keys(payload).length === 0) {
            onClose()
            return
        }
        try {
            await updateChild({ id: child._id, payload })
            onClose()
        } catch {
            // The mutation already reports the failure; keep the form open so
            // the correction isn't lost.
        }
    }

    const genderOptions = withCurrentValue(GENDER_OPTIONS, child.gender)
    const bloodGroupOptions = withCurrentValue(BLOOD_GROUP_OPTIONS, child.bloodGroup)
    const genotypeOptions = withCurrentValue(GENOTYPE_OPTIONS, child.genotype)

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    Edit child
                    <Text fontSize="sm" fontWeight="normal" color="gray.500">
                        {getChildName(child)}
                    </Text>
                </ModalHeader>
                <ModalCloseButton />
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <ModalBody>
                        <Stack spacing={4}>
                            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                                <FormControl isInvalid={Boolean(errors.firstName)}>
                                    <FormLabel fontSize="sm">First name</FormLabel>
                                    <Input size="sm" {...register('firstName')} />
                                    <FormErrorMessage fontSize="xs">
                                        {errors.firstName?.message}
                                    </FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={Boolean(errors.lastName)}>
                                    <FormLabel fontSize="sm">Surname</FormLabel>
                                    <Input size="sm" {...register('lastName')} />
                                    <FormErrorMessage fontSize="xs">
                                        {errors.lastName?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </SimpleGrid>

                            {/* The single most common correction: the two names
                                entered the wrong way round. One click beats
                                retyping both and getting it half-done. */}
                            <Button
                                alignSelf="flex-start"
                                size="xs"
                                variant="outline"
                                leftIcon={<FiRepeat />}
                                onClick={swapNames}
                            >
                                Swap first name and surname
                            </Button>

                            <FormControl isInvalid={Boolean(errors.middleName)}>
                                <FormLabel fontSize="sm">Middle name</FormLabel>
                                <Input size="sm" {...register('middleName')} />
                                <FormErrorMessage fontSize="xs">
                                    {errors.middleName?.message}
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={Boolean(errors.dateOfBirth)}>
                                <FormLabel fontSize="sm">Date of birth</FormLabel>
                                <Input
                                    size="sm"
                                    type="date"
                                    min={MIN_DATE_OF_BIRTH}
                                    max={todayInputValue()}
                                    {...register('dateOfBirth')}
                                />
                                <FormHelperText fontSize="xs">
                                    Drives the age group the child is checked into.
                                </FormHelperText>
                                <FormErrorMessage fontSize="xs">
                                    {errors.dateOfBirth?.message}
                                </FormErrorMessage>
                            </FormControl>

                            <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>
                                <FormControl isInvalid={Boolean(errors.gender)}>
                                    <FormLabel fontSize="sm">Gender</FormLabel>
                                    <Select
                                        size="sm"
                                        placeholder="Not set"
                                        {...register('gender')}
                                    >
                                        {genderOptions.map(({ value, label }) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage fontSize="xs">
                                        {errors.gender?.message}
                                    </FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={Boolean(errors.bloodGroup)}>
                                    <FormLabel fontSize="sm">Blood group</FormLabel>
                                    <Select
                                        size="sm"
                                        placeholder="Not set"
                                        {...register('bloodGroup')}
                                    >
                                        {bloodGroupOptions.map(({ value, label }) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage fontSize="xs">
                                        {errors.bloodGroup?.message}
                                    </FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={Boolean(errors.genotype)}>
                                    <FormLabel fontSize="sm">Genotype</FormLabel>
                                    <Select
                                        size="sm"
                                        placeholder="Not set"
                                        {...register('genotype')}
                                    >
                                        {genotypeOptions.map(({ value, label }) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage fontSize="xs">
                                        {errors.genotype?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </SimpleGrid>

                            <FormControl isInvalid={Boolean(errors.allergies)}>
                                <FormLabel fontSize="sm">Allergies</FormLabel>
                                <Input
                                    size="sm"
                                    placeholder="None"
                                    {...register('allergies')}
                                />
                                <FormHelperText fontSize="xs">
                                    Separate multiple allergies with a comma.
                                </FormHelperText>
                                <FormErrorMessage fontSize="xs">
                                    {errors.allergies?.message}
                                </FormErrorMessage>
                            </FormControl>
                        </Stack>
                    </ModalBody>
                    <ModalFooter>
                        <ButtonGroup spacing={3}>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={onClose}
                                isDisabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                type="submit"
                                isLoading={saving}
                                loadingText="Saving"
                            >
                                Save changes
                            </Button>
                        </ButtonGroup>
                    </ModalFooter>
                </form>
            </ModalContent>
        </Modal>
    )
}
